// Plan 24a, Task 2.6 — Lobby Quick Play matchmaking screen (Screen 5 — Quick Play variant)
//
// Panel (720px wide, padding: 'lg') with:
//   - Top: "QUICK PLAY MATCHMAKING" header (headline font, large text)
//   - Center left: 8-cell player slot grid (2 columns), populated from quick_play_joined
//   - Center right: locked preset display (read-only, opacity: 0.55)
//       ${QUICK_PLAY_PRESET.maxRounds} ROUNDS
//       ${QUICK_PLAY_PRESET.turnTimer}S TURN TIMER
//       ${QUICK_PLAY_PRESET.trophyCost} KEYS TROPHY COST
//   - Center: countdown display MM:SS (after quick_play_joined arrives)
//   - Bottom: LEAVE button (danger, small)
//   - No PAUSE button anywhere
//
// Countdown ticker uses gsapRegistry.create() + timeline.call() at 0.25s intervals
// so tests can control time via gsapRegistry.update(dt).
//
// Design system references:
//   - docs/design/tokens.json — color.player.*, spacing.sm, color.text.primary,
//     color.text.secondary, typography.family.headline, typography.size.caption
//   - docs/design/components.md — Panel §3, Button §1

import type { ClientMessage, ServerMessage } from '@shared/protocol';
import { QUICK_PLAY_PRESET } from '@shared/protocol/quick-play-preset';
import { createPanel } from '../components/panel';
import { createButton } from '../components/button';
import type { PanelHandle } from '../components/panel';
import type { UIComponentHandle } from '../components/types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { LobbyMountPayload } from './lobby-host';
import { buildWarmupSubscribeMessage, hasSeenWarmupTutorial } from '../../portal-entry';

// ─────────────────────────────────────────────────────────────────────────────
// Re-export public types needed by other screens
// ─────────────────────────────────────────────────────────────────────────────

export type { LobbyMountPayload };

// ─────────────────────────────────────────────────────────────────────────────
// Internal types
// ─────────────────────────────────────────────────────────────────────────────

interface QuickPlayPlayer {
  id: string;
  name: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants (tokens from docs/design/tokens.json)
// ─────────────────────────────────────────────────────────────────────────────

const MAX_PLAYERS = 8;
const PANEL_WIDTH = '720px';

// color.player.* from tokens.json
const PLAYER_COLORS: readonly string[] = [
  '#E04040', // red
  '#4080D0', // blue
  '#40B040', // green
  '#E8D020', // yellow
  '#E08820', // orange
  '#9040C0', // purple
  '#E06090', // pink
  '#40B0C0', // cyan
];

// spacing.sm = 8px (slot gap)
const SLOT_GAP = '8px';
// color.text.primary = #FFFFFF
const COLOR_TEXT_PRIMARY = '#FFFFFF';
// typography.family.headline
const FONT_HEADLINE =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";

// Maximum countdown (60s = 240 ticks at 0.25s)
const TICK_INTERVAL = 0.25;
const MAX_COUNTDOWN_S = 60;

// ─────────────────────────────────────────────────────────────────────────────
// MM:SS format helper
// ─────────────────────────────────────────────────────────────────────────────

function formatMmSs(ms: number): string {
  const remaining = Math.max(0, ms);
  const totalSec = Math.floor(remaining / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createLobbyQuickPlayScreen(
  ctx: ScreenMountContext<LobbyMountPayload>,
): ScreenHandle {
  const { gsapRegistry, host, payload, router } = ctx;

  let disposed = false;
  const players: QuickPlayPlayer[] = [];

  // Time-tracking for countdown (set when quick_play_joined arrives)
  let countdownMs = 0;
  let receivedAt = 0;

  // Inject-able time source (payload.now for test injection; defaults to performance.now)
  const now = payload.now ?? (() => performance.now());

  // Track button handles for cleanup
  const buttonHandles: UIComponentHandle[] = [];

  // Ticker handle (created lazily when quick_play_joined arrives)
  let tickHandle: GsapTimelineHandle | null = null;

  // ── Root element ─────────────────────────────────────────────────────────

  const rootEl = document.createElement('div');
  rootEl.className = 'screen-lobby-quick-play';
  rootEl.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'width: 100%',
    'height: 100%',
  ].join('; ');

  // ── Panel ─────────────────────────────────────────────────────────────────

  const panelHandle: PanelHandle = createPanel({
    gsapRegistry,
    mount: rootEl,
    padding: 'lg',
  });

  const panelEl = panelHandle.element;
  panelEl.style.width = PANEL_WIDTH;
  panelEl.style.boxSizing = 'border-box';

  // Panel content container
  const panelContent = document.createElement('div');
  panelContent.style.cssText = ['display: flex', 'flex-direction: column', 'gap: 20px'].join('; ');
  panelEl.appendChild(panelContent);

  // ── Top: "QUICK PLAY MATCHMAKING" header ─────────────────────────────────

  const headerEl = document.createElement('h2');
  headerEl.textContent = 'QUICK PLAY MATCHMAKING';
  headerEl.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    'font-size: var(--typography-size-h2, 32px)',
    'font-weight: 700',
    'text-align: center',
    'margin: 0',
    'letter-spacing: 2px',
  ].join('; ');
  panelContent.appendChild(headerEl);

  // ── Countdown display (hidden until quick_play_joined) ───────────────────

  const countdownDisplayEl = document.createElement('div');
  countdownDisplayEl.setAttribute('data-countdown', '');
  countdownDisplayEl.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    'font-size: var(--typography-size-h1, 48px)',
    'font-weight: 700',
    'text-align: center',
    'letter-spacing: 4px',
    'visibility: hidden',
  ].join('; ');
  countdownDisplayEl.textContent = '0:00';
  panelContent.appendChild(countdownDisplayEl);

  // ── Center row: slot grid (left) + locked preset column (right) ──────────

  const centerRow = document.createElement('div');
  centerRow.style.cssText = ['display: flex', `gap: 16px`, 'align-items: flex-start'].join('; ');
  panelContent.appendChild(centerRow);

  // ── Slot grid container (left ~60%) ──────────────────────────────────────

  const gridEl = document.createElement('div');
  gridEl.className = 'lobby-quick-play__slot-grid';
  gridEl.style.cssText = ['display: flex', 'flex-wrap: wrap', `gap: ${SLOT_GAP}`, 'flex: 6'].join(
    '; ',
  );
  centerRow.appendChild(gridEl);

  // ── Locked preset column (right ~40%) ─────────────────────────────────────

  const presetCol = document.createElement('div');
  presetCol.className = 'lobby-quick-play__preset-col';
  presetCol.setAttribute('data-preset-panel', '');
  presetCol.style.cssText = [
    'flex: 4',
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'gap: 12px',
    // Locked / read-only indicator
    'opacity: 0.55',
    'pointer-events: none',
  ].join('; ');
  centerRow.appendChild(presetCol);

  // Preset label helper
  function buildPresetLine(text: string): HTMLElement {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = [
      `font-family: ${FONT_HEADLINE}`,
      `color: ${COLOR_TEXT_PRIMARY}`,
      'font-size: var(--typography-size-h5, 18px)',
      'font-weight: 700',
      'text-align: center',
      'letter-spacing: 1px',
    ].join('; ');
    return el;
  }

  presetCol.appendChild(buildPresetLine(`${QUICK_PLAY_PRESET.maxRounds} ROUNDS`));
  presetCol.appendChild(buildPresetLine(`${QUICK_PLAY_PRESET.turnTimer}S TURN TIMER`));
  presetCol.appendChild(buildPresetLine(`${QUICK_PLAY_PRESET.trophyCost} KEYS TROPHY COST`));

  // ── Panel bottom: LEAVE button ────────────────────────────────────────────

  const panelBottom = document.createElement('div');
  panelBottom.style.cssText = ['display: flex', 'justify-content: center'].join('; ');
  panelContent.appendChild(panelBottom);

  const leaveHandle = createButton({
    gsapRegistry,
    mount: panelBottom,
    variant: 'danger',
    size: 'small',
    label: 'BACK',
    disabled: false,
    onClick: () => {
      if (disposed) return;
      payload.send({ type: 'leave' });
      payload.send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
      router.navigate('warmup-arena-overlay', { portal: null });
    },
  });
  leaveHandle.element.setAttribute('data-action', 'leave');
  buttonHandles.push(leaveHandle);

  function recoverAfterMaintenanceRejection(): void {
    const target = payload.escBackTarget;
    if (target != null) {
      target.beforeNavigate?.();
      router.navigate(target.screenId, target.payload);
      return;
    }
    payload.send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Slot rendering
  // ─────────────────────────────────────────────────────────────────────────

  /** Build one filled player slot cell. */
  function buildFilledSlot(player: QuickPlayPlayer, index: number): HTMLElement {
    const slotEl = document.createElement('div');
    slotEl.setAttribute('data-player-slot', player.id);
    slotEl.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'gap: 6px',
      'width: calc(50% - 4px)',
      'min-height: 100px',
      'box-sizing: border-box',
      'padding: 12px 8px',
      'background: rgba(0,0,0,0.20)',
      'border-radius: 10px',
    ].join('; ');

    // Colored circle avatar
    const avatarEl = document.createElement('div');
    const playerColor = PLAYER_COLORS[index % PLAYER_COLORS.length] ?? '#FFFFFF';
    avatarEl.style.cssText = [
      'width: 40px',
      'height: 40px',
      `background: ${playerColor}`,
      'border-radius: 50%',
      'flex-shrink: 0',
    ].join('; ');
    slotEl.appendChild(avatarEl);

    // Player name
    const nameEl = document.createElement('div');
    nameEl.textContent = player.name;
    nameEl.style.cssText = [
      `color: ${COLOR_TEXT_PRIMARY}`,
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 14px',
      'font-weight: 700',
      'text-align: center',
      'max-width: 100%',
      'overflow: hidden',
      'text-overflow: ellipsis',
      'white-space: nowrap',
    ].join('; ');
    slotEl.appendChild(nameEl);

    return slotEl;
  }

  /** Build one empty slot cell — plain placeholder, no buttons. */
  function buildEmptySlot(): HTMLElement {
    const slotEl = document.createElement('div');
    slotEl.className = 'lobby-quick-play__empty-slot';
    slotEl.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'width: calc(50% - 4px)',
      'min-height: 100px',
      'box-sizing: border-box',
      'background: rgba(0,0,0,0.10)',
      'border-radius: 10px',
      'border: 1px dashed rgba(255,255,255,0.15)',
    ].join('; ');
    return slotEl;
  }

  /** Re-render all 8 slot cells from current player state. */
  function renderSlots(): void {
    while (gridEl.firstChild) {
      gridEl.removeChild(gridEl.firstChild);
    }

    const occupiedCount = players.length;

    for (let i = 0; i < occupiedCount; i++) {
      const player = players[i]!;
      const slotEl = buildFilledSlot(player, i);
      gridEl.appendChild(slotEl);
    }

    for (let i = occupiedCount; i < MAX_PLAYERS; i++) {
      const slotEl = buildEmptySlot();
      gridEl.appendChild(slotEl);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Countdown ticker (driven by gsapRegistry, NOT setInterval)
  //
  // Uses timeline.call() at fixed 0.25s intervals — the proven codebase pattern
  // (see src/client/ui/components/countdown.ts). The registry uses .time(elapsed)
  // to seek, so tl.call(fn, undefined, absoluteSeconds) fires reliably.
  //
  // The ticker handle is created lazily when quick_play_joined arrives.
  // ─────────────────────────────────────────────────────────────────────────

  /** Update the countdown display element from current time state. */
  function updateCountdown(): void {
    if (disposed) return;
    const remainingMs = Math.max(0, receivedAt + countdownMs - now());
    countdownDisplayEl.textContent = formatMmSs(remainingMs);
  }

  /**
   * Creates a GSAP ticker handle that fires updateCountdown() at 0.25s intervals
   * for up to MAX_COUNTDOWN_S seconds. Called once when quick_play_joined arrives.
   */
  function startCountdownTicker(): GsapTimelineHandle {
    const handle = gsapRegistry.create('quick-play:ticker');
    const tl = handle.timeline;

    // Schedule calls at 0.25, 0.5, 0.75, ..., MAX_COUNTDOWN_S seconds
    // Using explicit absolute positions — same pattern as countdown.ts scheduler
    const totalTicks = Math.ceil(MAX_COUNTDOWN_S / TICK_INTERVAL);
    for (let i = 1; i <= totalTicks; i++) {
      const absoluteSec = i * TICK_INTERVAL;
      tl.call(updateCountdown, undefined, absoluteSec);
    }

    return handle;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Server message handler
  // ─────────────────────────────────────────────────────────────────────────

  const unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (disposed) return;

    if (msg.type === 'quick_play_joined') {
      // Capture received-at timestamp using the injectable time source
      receivedAt = now();
      countdownMs = Math.max(0, msg.countdownMs);

      // Populate players from the full players array (NOT incremental player_joined)
      players.length = 0;
      for (const p of msg.players) {
        players.push({ id: p.id, name: p.name });
      }
      renderSlots();

      // Show countdown display with initial value
      countdownDisplayEl.style.visibility = 'visible';
      updateCountdown();

      // Start the GSAP ticker (kill any prior ticker if quick_play_joined fires twice)
      if (tickHandle) {
        tickHandle.dispose();
      }
      tickHandle = startCountdownTicker();

      return;
    }

    if (msg.type === 'notification' && msg.text === 'maintenance_update_pending') {
      recoverAfterMaintenanceRejection();
      return;
    }

    // All other messages: no-op for Quick Play screen
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Mount
  // ─────────────────────────────────────────────────────────────────────────

  // Initial render of empty slots
  renderSlots();

  // Append root to host and trigger panel entrance animation
  host.appendChild(rootEl);
  panelHandle.show();

  // ─────────────────────────────────────────────────────────────────────────
  // Send quick_play_request immediately on mount
  // ─────────────────────────────────────────────────────────────────────────

  if (payload.skipInitialRequest !== true) {
    payload.send({
      type: 'quick_play_request',
      playerName: payload.playerName,
      ...(payload.preferredCustomization === undefined
        ? {}
        : { customization: payload.preferredCustomization }),
    } satisfies ClientMessage);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // dispose
  // ─────────────────────────────────────────────────────────────────────────

  function disposeButtonHandles(): void {
    for (const h of buttonHandles) {
      h.dispose();
    }
    buttonHandles.length = 0;
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    tickHandle?.dispose();
    tickHandle = null;
    unsubscribe();
    disposeButtonHandles();
    panelHandle.dispose();

    if (rootEl.parentNode) {
      rootEl.parentNode.removeChild(rootEl);
    }
  }

  return {
    element: rootEl,
    dispose,
  };
}
